import { useEffect, useState } from 'react';
import {
  Modal,
  Form,
  Input,
  Select,
  Upload,
  Button,
  message,
  Space,
  Row,
  Col,
  Typography
} from 'antd';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { UploadOutlined } from '@ant-design/icons';
import type { UploadFile } from 'antd/es/upload/interface';

import { MaintenanceRequest, MaintenanceStatus, MaintenancePriority } from '@/types/maintenance';
import { addMaintenanceRequest, updateMaintenanceRequest } from '@/api/maintenance';
import { useAuth } from '@/contexts/AuthContext';
import { BlockApartmentUnitSelector } from '@/components/common/BlockApartmentUnitSelector';
import { ResidentSelector } from '@/components/common/ResidentSelector';
import { EmployeeSelector } from '@/components/common/EmployeeSelector';
import type { FileMetadata } from '@/types/common';

const { TextArea } = Input;
const { Title } = Typography;

interface MaintenanceModalProps {
  open: boolean;
  maintenanceRequest?: MaintenanceRequest | null;
  onSuccess: () => void;
  onCancel: () => void;
}

const MAINTENANCE_STATUSES: { value: MaintenanceStatus; label: string }[] = [
  { value: 'Open', label: 'Open' },
  { value: 'In Progress', label: 'In Progress' },
  { value: 'On Hold', label: 'On Hold' },
  { value: 'Resolved', label: 'Resolved' }
];

const MAINTENANCE_PRIORITIES: { value: MaintenancePriority; label: string }[] = [
  { value: 'Low', label: 'Low' },
  { value: 'Medium', label: 'Medium' },
  { value: 'High', label: 'High' },
  { value: 'Urgent', label: 'Urgent' }
];

// Helper functions to get translation keys
const getStatusTranslationKey = (status: string): string => {
  const statusMap: Record<string, string> = {
    'Open': 'maintenance:status.open',
    'In Progress': 'maintenance:status.in_progress',
    'On Hold': 'maintenance:status.on_hold',
    'Resolved': 'maintenance:status.resolved'
  };
  return statusMap[status] || `maintenance:status.${status.toLowerCase().replace(' ', '_')}`;
};

const getPriorityTranslationKey = (priority: string): string => {
  const priorityMap: Record<string, string> = {
    'Low': 'maintenance:priority.low',
    'Medium': 'maintenance:priority.medium',
    'High': 'maintenance:priority.high',
    'Urgent': 'maintenance:priority.urgent'
  };
  return priorityMap[priority] || `maintenance:priority.${priority.toLowerCase()}`;
};

export default function MaintenanceModal({ open, maintenanceRequest, onCancel }: MaintenanceModalProps) {
  const { t } = useTranslation(['maintenance', 'common']);
  const { firestoreUser } = useAuth();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [blockData, setBlockData] = useState<{ id: string; name: string } | null>(null);
  const [apartmentUnitData, setApartmentUnitData] = useState<{ id: string; name: string } | null>(null);
  const [reportedByData, setReportedByData] = useState<{ id: string; name: string } | null>(null);
  const [assignedToData, setAssignedToData] = useState<{ id: string; name: string } | null>(null);

  // Mutations
  const addMaintenanceMutation = useMutation({
    mutationFn: (data: Omit<MaintenanceRequest, 'id' | 'createdAt' | 'updatedAt'>) => 
      addMaintenanceRequest(data, firestoreUser!),
    onSuccess: () => {
      message.success(t('maintenance:addSuccess'));
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Add maintenance request error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to add maintenance request: ${errorMessage}`);
    }
  });

  const updateMaintenanceMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Omit<MaintenanceRequest, 'id' | 'createdAt' | 'updatedAt'>> }) => 
      updateMaintenanceRequest(id, data, firestoreUser!),
    onSuccess: () => {
      message.success(t('maintenance:updateSuccess'));
      queryClient.invalidateQueries({ queryKey: ['maintenance'] });
      handleCancel();
    },
    onError: (error: any) => {
      console.error('Update maintenance request error:', error);
      const errorMessage = error?.message || error?.toString() || t('common:errorOccurred');
      message.error(`Failed to update maintenance request: ${errorMessage}`);
    }
  });

  // Initialize form when maintenance request changes
  useEffect(() => {
    if (maintenanceRequest) {
      form.setFieldsValue({
        title: maintenanceRequest.title,
        description: maintenanceRequest.description,
        status: maintenanceRequest.status,
        priority: maintenanceRequest.priority,
        blockId: maintenanceRequest.block?.id,
        apartmentUnitId: maintenanceRequest.apartmentUnit?.id,
        reportedById: maintenanceRequest.reportedBy?.id,
        assignedToId: maintenanceRequest.assignedTo?.id,
        resolutionNotes: maintenanceRequest.resolutionNotes
      });

      // Set related data
      setBlockData(maintenanceRequest.block || null);
      setApartmentUnitData(maintenanceRequest.apartmentUnit || null);
      setReportedByData(maintenanceRequest.reportedBy || null);
      setAssignedToData(maintenanceRequest.assignedTo || null);

      // Set file list
      if (maintenanceRequest.attachments) {
        const files: UploadFile[] = maintenanceRequest.attachments.map((attachment, index) => ({
          uid: `-${index}`,
          name: attachment.name,
          status: 'done',
          url: attachment.url
        }));
        setFileList(files);
      }
    } else {
      form.resetFields();
      setFileList([]);
      setBlockData(null);
      setApartmentUnitData(null);
      setReportedByData(null);
      setAssignedToData(null);
    }
  }, [maintenanceRequest, form]);

  const handleSubmit = async (values: any) => {
    if (!firestoreUser) {
      message.error(t('common:notAuthenticated'));
      return;
    }

    // Additional client-side validation
    if (!values.title?.trim()) {
      message.error(t('maintenance:titleRequired'));
      return;
    }
    if (!values.description?.trim()) {
      message.error(t('maintenance:descriptionRequired'));
      return;
    }
    if (!values.status) {
      message.error(t('maintenance:statusRequired'));
      return;
    }
    if (!values.priority) {
      message.error(t('maintenance:priorityRequired'));
      return;
    }
    if (!values.blockId) {
      message.error(t('maintenance:blockRequired'));
      return;
    }
    if (!values.reportedById) {
      message.error(t('maintenance:reportedByRequired'));
      return;
    }

    try {
      // Convert file list to FileMetadata
      const attachments: FileMetadata[] = fileList
        .filter(file => file.status === 'done')
        .map(file => ({
          id: file.uid || `file-${Date.now()}-${Math.random()}`,
          name: file.name || '',
          url: file.url || '',
          size: file.size || 0,
          type: file.type || '',
          path: file.url || '',
          uploadedAt: new Date(),
          mimeType: file.type || ''
        }));

      const maintenanceData: Omit<MaintenanceRequest, 'id' | 'createdAt' | 'updatedAt'> = {
        title: values.title.trim(),
        description: values.description.trim(),
        status: values.status,
        priority: values.priority,
        reportedBy: reportedByData || { id: '', name: '' },
        block: blockData || { id: '', name: '' },
        apartmentUnit: apartmentUnitData || undefined,
        assignedTo: assignedToData || undefined,
        resolutionNotes: values.resolutionNotes?.trim() || undefined,
        attachments: attachments.length > 0 ? attachments : undefined,
        comments: [],
        workspace: firestoreUser!.workspace!
      };

      if (maintenanceRequest) {
        updateMaintenanceMutation.mutate({ id: maintenanceRequest.id, data: maintenanceData });
      } else {
        addMaintenanceMutation.mutate(maintenanceData);
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      message.error(`Form submission failed: ${error?.message || 'Unknown error'}`);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setFileList([]);
    setBlockData(null);
    setApartmentUnitData(null);
    setReportedByData(null);
    setAssignedToData(null);
    onCancel();
  };

  const handleFileChange = ({ fileList: newFileList }: { fileList: UploadFile[] }) => {
    setFileList(newFileList);
  };

  const handleBlockDataChange = (block: { id: string; name: string } | null) => {
    setBlockData(block);
  };

  const handleApartmentUnitDataChange = (apartmentUnit: { id: string; name: string } | null) => {
    setApartmentUnitData(apartmentUnit);
  };

  const handleReportedByDataChange = (reportedBy: { id: string; name: string } | null) => {
    setReportedByData(reportedBy);
  };

  const handleAssignedToDataChange = (assignedTo: { id: string; name: string } | null) => {
    setAssignedToData(assignedTo);
  };

  const uploadProps = {
    beforeUpload: () => false, // Prevent auto upload
    fileList,
    onChange: handleFileChange,
    multiple: true
  };

  return (
    <Modal
      title={
        <Title level={4} style={{ margin: 0 }}>
          {maintenanceRequest ? t('maintenance:editRequest') : t('maintenance:createRequest')}
        </Title>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={800}
      destroyOnHidden
    >
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{
          status: 'Open',
          priority: 'Medium'
        }}
      >
        {/* Title */}
        <Form.Item
          name="title"
          label={t('maintenance:title')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <Input placeholder={t('maintenance:titlePlaceholder')} />
        </Form.Item>

        {/* Description */}
        <Form.Item
          name="description"
          label={t('maintenance:description')}
          rules={[{ required: true, message: t('common:fieldRequired') }]}
        >
          <TextArea 
            rows={4} 
            placeholder={t('maintenance:descriptionPlaceholder')} 
          />
        </Form.Item>

        {/* Status and Priority */}
        <Row gutter={16}>
          <Col span={12}>
            <Form.Item
              name="status"
              label={t('maintenance:statusLabel')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('maintenance:selectStatus')}>
                {MAINTENANCE_STATUSES.map(status => (
                  <Select.Option key={status.value} value={status.value}>
                    {t(getStatusTranslationKey(status.value))}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              name="priority"
              label={t('maintenance:priorityLabel')}
              rules={[{ required: true, message: t('common:fieldRequired') }]}
            >
              <Select placeholder={t('maintenance:selectPriority')}>
                {MAINTENANCE_PRIORITIES.map(priority => (
                  <Select.Option key={priority.value} value={priority.value}>
                    {t(getPriorityTranslationKey(priority.value))}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>

        {/* Block and Apartment Unit */}
        <BlockApartmentUnitSelector
          onBlockDataChange={handleBlockDataChange}
          onApartmentUnitDataChange={handleApartmentUnitDataChange}
        />

        {/* Reported By */}
        <ResidentSelector
          fieldName="reportedById"
          onResidentDataChange={handleReportedByDataChange}
        />

        {/* Assigned To */}
        <EmployeeSelector
          fieldName="assignedToId"
          onEmployeeDataChange={handleAssignedToDataChange}
        />

        {/* Resolution Notes */}
        <Form.Item
          name="resolutionNotes"
          label={t('maintenance:resolutionNotes')}
        >
          <TextArea 
            rows={3} 
            placeholder={t('maintenance:resolutionNotesPlaceholder')} 
          />
        </Form.Item>

        {/* Attachments */}
        <Form.Item
          label={t('maintenance:attachments')}
        >
          <Upload {...uploadProps}>
            <Button icon={<UploadOutlined />}>
              {t('common:upload')}
            </Button>
          </Upload>
        </Form.Item>

        <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
          <Space>
            <Button onClick={handleCancel}>
              {t('common:actions.cancel')}
            </Button>
            <Button 
              type="primary" 
              htmlType="submit"
              loading={addMaintenanceMutation.isPending || updateMaintenanceMutation.isPending}
            >
              {maintenanceRequest ? t('common:actions.update') : t('common:actions.add')}
            </Button>
          </Space>
        </Form.Item>
      </Form>
    </Modal>
  );
}